// Material ui components
import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Typography,
} from '@material-ui/core';
import { useMutation, useQuery } from 'react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import ArticleAddons from './ArticleAddons';
import ArticleAuthorDetails from './ArticleAuthorDetails';
import ArticleDetails from './ArticleDetails';
import ArticleMoreNews from './ArticleMoreNews';
import ArticleRelatedSubjects from './ArticleRelatedSubjects';
import ArticleSocialMedias from './ArticleSocialMedias';
import Comments from './Comments';
import CommentForm from './CommentsForm';
import { articleStyles } from './styles';
import breakDefault from '../../assets/imgs/articles/detail.png';
// Global components
import BannerAdvertisement from '../../components/BannerAdvertisement';
import { If } from '../../components/If';
import ImageTitleTop from '../../components/ImageTitleTop';
import MetaTags from '../../components/Seo/MetaTags';
import Subheader from '../../components/Subheader';
import { useAuthContext } from '../../context/AuthContextProvider';
import {
  createComment,
  deleteComment,
  getArticle,
  getComments,
  updateComment,
} from '../../services/article';
import { getProfessionalPhotoByOwner } from '../../services/professional';
import { Article as ArticleType } from '../../types/article';
import { generateHash } from '../../utils/crypto';
import { useSessionStorage } from '../../utils/hooks';
import { getLargestImageUrl } from '../../utils/image';
import { normalizeContent } from '../../utils/normalize-content';
import { isBot } from '../../utils/regex';
import { ScrollToTop } from '../../utils/scroll';
// Local components

export default function Article() {
  const [articlesIds, setArticlesIds] = useSessionStorage(
    '@App:canReadArticles',
    [],
  );
  const classes = articleStyles();
  const commentRef = useRef<HTMLDivElement>(null);
  const [article, setArticle] = useState<ArticleType | null>(null);
  const { auth } = useAuthContext();
  const { hashId } = useParams<{ hashId: string }>();
  const [encodeHashId, setEncondeHashId] = useState<string | null>(null);
  const [enabledRequest, setEnabledRequest] = useState(false);
  const [avatarUser, setAvatarUser] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentError, setCommentError] = useState({
    status: false,
    message: '',
  });
  const hasPermission =
    (auth.isLoggedIn &&
      article?.plans.some(
        (plan) => plan.type === auth.user?.subscription?.plan?.type,
      )) ||
    (auth.isLoggedIn && article?.plans.length === 0) ||
    (articlesIds?.some((a: string) => a === encodeHashId) &&
      article?.plans?.length === 0);

  const maxArticles = isBot(navigator.userAgent) ? Infinity : 1;

  const { isLoading: loading } = useQuery(
    ['article', hashId],
    () => getArticle(hashId, hasPermission),
    {
      onSuccess(data: ArticleType) {
        setArticle(data);
      },
      enabled: enabledRequest,
    },
  );

  const { isLoading: loadingComments, refetch } = useQuery(
    ['comments', hashId],
    () => getComments(hashId),
    {
      onSuccess: (data) => {
        setComments(data.filter((comment: any) => comment.blocked === false));
      },
    },
  );

  const { isLoading: loadingCurrentUser } = useQuery(
    ['professionalPhoto', auth?.user?.id],
    () => getProfessionalPhotoByOwner(auth?.user?.id ?? 0),
    {
      enabled: !!auth.user?.id,
      onSuccess: (data) => {
        setAvatarUser(data[0]?.photo?.formats?.thumbnail?.url);
      },
    },
  );

  const addComments = useMutation<
    { data: any },
    any,
    { articleId: string; comment: { content: string; threadOf?: string } }
  >((data) => createComment(data), {
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      setCommentError({
        status: true,
        message: 'Não foi possível publicar o comentário',
      });
      commentRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
  });

  const editComments = useMutation<
    { data: any },
    any,
    { articleId: string; comment: { content: string; id: string } }
  >((data) => updateComment(data), {
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      setCommentError({
        status: true,
        message: 'Não foi possível editar o comentário',
      });
      commentRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
  });

  const removeComments = useMutation<
    any,
    any,
    { articleId: string; id: string; authorId: string }
  >((data) => deleteComment(data), {
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      setCommentError({
        status: true,
        message: 'Não foi possível deletar o comentário',
      });
      commentRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
  });

  const headerImage = getLargestImageUrl(article?.headerImage);

  useEffect(() => {
    generateHash(hashId).then((result) => setEncondeHashId(result));
  }, [hashId]);

  useEffect(() => {
    if (!encodeHashId) return;

    if (
      articlesIds.length < maxArticles &&
      !articlesIds.includes(encodeHashId) &&
      !auth.isLoggedIn
    ) {
      setArticlesIds((prev: string[]) => [...prev, encodeHashId]);
    }

    setEnabledRequest((prevState) => !prevState);
  }, [encodeHashId]);

  const handleAddComment = (values: { content: string }) => {
    addComments.mutateAsync({
      articleId: hashId,
      comment: {
        ...values,
      },
    });
  };

  const handleReplyComment = (values: {
    content: string;
    threadOf: string;
  }) => {
    addComments.mutateAsync({
      articleId: hashId,
      comment: values,
    });
  };

  const handleEditComment = (values: { content: string; id: string }) => {
    editComments.mutateAsync({
      articleId: hashId,
      comment: values,
    });
  };

  const handleRemoveComment = (id: string, authorId: string) => {
    removeComments.mutateAsync({
      articleId: hashId,
      id,
      authorId,
    });
  };

  const noPermissionIntrotext = article?.introtext
    ? `${article.introtext.substring(0, 300)}...`
    : '';

  if (loading) return <CircularProgress color="secondary" />;
  if (!article) return <>Artigo não encontrado</>;

  const articleHref = `/artigo/${article.slug}/${hashId}`;
  const loginHref = `/login?redirectUrl=${encodeURIComponent(articleHref)}`;
  const paywallPlansHref = `/planos?${new URLSearchParams({
    utm_source: 'plataforma',
    utm_medium: 'paywall',
    utm_campaign: article?.slug ?? 'pagina-artigo',
  }).toString()}`;
  const isPlanRestricted = article.plans.length > 0;
  const restrictedPlansText = new Intl.ListFormat('pt-BR', {
    style: 'long',
    type: 'conjunction',
  }).format(article.plans.map((plan) => plan.name));
  const isLoggedWithoutRequiredPlan = auth.isLoggedIn && isPlanRestricted;

  return (
    <ScrollToTop>
      <Box component="section">
        <MetaTags
          title={article.title}
          description={article.introtext}
          imageUrl={headerImage}
          type="article"
          seo={article.seo}
          article={{
            publishedTime: article.publishedAt,
            modifiedTime: article.updatedAt,
            author: article.author?.fullName,
            section: article.markets.map((market) => market.name).join(', '),
            expirationTime: article.updatedAt,
            tag: article.markets.map((market) => market.name).join(', '),
          }}
        />
        <Subheader
          title="Artigo"
          favoriteType="article"
          breadcrumbs1={
            <Link
              component={RouterLink}
              to="/artigos"
              underline="none"
              className={classes.link}>
              Artigos
            </Link>
          }
          breadcrumbs2={article.title}
        />
        <Paper square className={classes.container}>
          <Grid container spacing={2}>
            <Grid item container md={8}>
              <Grid item xs={12}>
                <ImageTitleTop
                  image={headerImage ?? breakDefault}
                  title={article.title}
                  type={new Intl.ListFormat('pt-BR', {
                    style: 'long',
                    type: 'conjunction',
                  }).format(article.markets.map((market) => market.name))}
                />
                <ArticleDetails
                  author={article.author}
                  categories={article.categories}
                  markets={article.markets}
                  publishedAt={article.publishedAt}
                  updatedAt={article.updatedAt}
                  estimatedReadingTime={article.estimatedReadingTime}
                />
                <Divider className={classes.dividerSubtitle} />
                <If condition={hasPermission}>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: normalizeContent(
                        article.introtext ?? '',
                        classes,
                      ),
                    }}
                    component="h2"
                  />
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: normalizeContent(article.content ?? '', classes),
                    }}
                  />
                  <If condition={article.addons}>
                    <ArticleAddons addons={article.addons} />
                  </If>
                  <If condition={article.author}>
                    <ArticleAuthorDetails author={article.author} />
                  </If>
                </If>
                <If condition={!hasPermission}>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: normalizeContent(noPermissionIntrotext, classes),
                    }}
                    component="h2"
                  />
                  <Box className={classes.payWallSpacer} aria-hidden="true">
                    <Box className={classes.payWallArticlePreview}>
                      <Typography>
                        A análise completa apresenta contexto, dados de mercado,
                        interpretação dos principais movimentos e pontos de
                        atenção para apoiar decisões estratégicas. O conteúdo
                        segue disponível apenas para usuários com acesso
                        autorizado pela Uqbar.
                      </Typography>
                      <Typography>
                        Continue lendo para acessar o estudo, a visão editorial
                        e os complementos relacionados a este artigo. Faça login
                        ou conheça os planos elegíveis para liberar a leitura
                        integral.
                      </Typography>
                      <Typography>
                        A cobertura reúne informações institucionais, tendências
                        do setor e referências de inteligência para apoiar a
                        compreensão do tema apresentado neste artigo.
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    aria-label="Acesso exclusivo ao artigo"
                    className={classes.payWall}
                    role="region">
                    <Box className={classes.payWallContent}>
                      <Box className={classes.payWallHeader}>
                        <Typography className={classes.payWallBadge}>
                          Conteúdo exclusivo
                        </Typography>
                        <Typography variant="h5" component="p">
                          Continue lendo com acesso Uqbar
                        </Typography>
                        <Typography color="textSecondary">
                          {isLoggedWithoutRequiredPlan
                            ? `Este artigo é exclusivo para membros dos planos ${restrictedPlansText}. Conheça as opções disponíveis para liberar este conteúdo.`
                            : 'Este artigo é exclusivo para assinantes e planos elegíveis. Entre na sua conta ou assine para acessar a análise completa.'}
                        </Typography>
                      </Box>
                      <Grid
                        container
                        spacing={2}
                        className={classes.payWallCards}>
                        {!auth.isLoggedIn && (
                          <Grid item xs={12} sm={6}>
                            <Box className={classes.payWallCard}>
                              <Typography variant="subtitle1" component="p">
                                Já tem conta?
                              </Typography>
                              <Typography color="textSecondary">
                                Faça login para verificar seu acesso e continuar
                                a leitura do artigo.
                              </Typography>
                              <Button
                                className={classes.payWallButton}
                                color="secondary"
                                component={RouterLink}
                                fullWidth
                                to={loginHref}
                                variant="contained">
                                Entrar e continuar lendo
                              </Button>
                            </Box>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={auth.isLoggedIn ? 12 : 6}>
                          <Box className={classes.payWallCard}>
                            <Typography variant="subtitle1" component="p">
                              {auth.isLoggedIn
                                ? 'Seu plano atual não inclui este conteúdo'
                                : 'Ainda não é assinante?'}
                            </Typography>
                            <Typography color="textSecondary">
                              {auth.isLoggedIn
                                ? 'Compare os planos disponíveis e escolha a opção com acesso a artigos e pesquisas exclusivos.'
                                : 'Conheça os planos Uqbar para acessar artigos, pesquisas e inteligência de mercado.'}
                            </Typography>
                            <Button
                              className={
                                auth.isLoggedIn
                                  ? classes.payWallButton
                                  : classes.payWallOutlineButton
                              }
                              color="secondary"
                              component={RouterLink}
                              fullWidth
                              to={paywallPlansHref}
                              variant={
                                auth.isLoggedIn ? 'contained' : 'outlined'
                              }>
                              {auth.isLoggedIn
                                ? 'Conhecer planos'
                                : 'Ver planos / assinar'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </If>
              </Grid>
            </Grid>
            <Grid item xs={12} md>
              <Grid item xs={12}>
                <BannerAdvertisement location="Article_Area1" />
              </Grid>
              <Grid item xs={12}>
                <Box py={2}>
                  <Typography>
                    <b>&mdash;Compartilhe o artigo</b>
                  </Typography>
                  <Divider className={classes.divider} />
                  <Box p={1} display="flex" alignItems="center" width="100%">
                    <ArticleSocialMedias
                      title={article.title}
                      slug={article.slug}
                      hashId={article.id}
                    />
                  </Box>
                </Box>
              </Grid>
              <If
                condition={
                  article?.relatedInstitutions?.length ||
                  article?.relatedProfessionals?.length ||
                  article?.relatedOperations?.length
                }>
                <Grid item xs={12}>
                  <Box py={2}>
                    <Typography>
                      <b>&mdash;Neste Artigo</b>
                    </Typography>
                    <Divider className={classes.divider} />
                    <ArticleRelatedSubjects
                      relatedInstitutions={article?.relatedInstitutions}
                      relatedProfessionals={article?.relatedProfessionals}
                      relatedOperations={article?.relatedOperations}
                    />
                  </Box>
                </Grid>
              </If>
              <Grid item xs={12}>
                <Box py={2}>
                  <Typography>
                    <b>&mdash;Mais Notícias do Mercado</b>
                  </Typography>
                  <Divider className={classes.divider} />
                  {article?.markets?.length > 0 &&
                    article?.markets[0]?.name && (
                      <ArticleMoreNews
                        articleId={hashId}
                        market={article.markets[0].name}
                      />
                    )}
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={12} md={8} innerRef={commentRef}>
              <Typography>
                <b>&mdash; O que você achou do artigo?</b>
              </Typography>
              <Divider className={classes.divider} />
              <Box style={{ padding: '1rem 0' }}>
                <CommentForm
                  handleSubmit={handleAddComment}
                  loading={addComments.isLoading || loadingCurrentUser}
                  submitLabel="Comentar"
                  currentUserAvatar={avatarUser}
                  isComment={true}
                  placeholder="Digite seu comentário"
                  commentError={commentError}
                  setCommentError={setCommentError}
                  commentId=""
                  threadId=""
                  handleClose={() => {}}
                  content=""
                />
              </Box>
              <Typography>
                <b>&mdash;Comentários</b>
              </Typography>
              <Divider className={classes.divider} />
              <If condition={loadingComments}>
                <CircularProgress color="secondary" />
              </If>
              <If condition={!loadingComments}>
                {comments.length ? (
                  comments.map((comment: any) => (
                    <Comments
                      key={comment?.id}
                      threadId={comment.id}
                      classes={classes}
                      comment={comment}
                      handleReplyComment={handleReplyComment}
                      handleEditComment={handleEditComment}
                      handleRemoveComment={handleRemoveComment}
                      loading={addComments.isLoading || editComments.isLoading}
                      commentError={commentError}
                      setCommentError={setCommentError}
                      hasPermission={
                        auth.isLoggedIn ||
                        articlesIds?.some((a: string) => a === encodeHashId)
                      }
                    />
                  ))
                ) : (
                  <Box p={2}>
                    <Typography>
                      Seja o primeiro a comentar neste artigo
                    </Typography>
                  </Box>
                )}
              </If>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </ScrollToTop>
  );
}
